import { useAtom } from '@reatom/npm-react'
import { format } from 'date-fns'
import styled from 'styled-components'

import { Numeric } from '@blade/ui-kit'
import { CurrencyModel } from '@root/entities/currency'
import { Trend } from '@root/entities/trend'
import { trendColor } from '@root/entities/trend/ui'
import { DATE_FORMAT } from '@root/shared/core'
import { useTranslation } from '@root/shared/localization'

import type { AggregatedPositionModel } from '../../atoms'
import type { PnlNative, PnlUsd } from '../../atoms/reatom-position-pnl'
import type { Pair } from '@root/entities/pair'

type Props = {
  pnlNative: PnlNative
  pnlUsd: PnlUsd
  pair: Pair.PairMinimal
  position: AggregatedPositionModel
}

export const PositionSize: React.FC<Props> = ({
  pnlNative,
  pnlUsd,
  pair,
  position,
}) => {
  const { t } = useTranslation()

  const nativeCurrency = CurrencyModel.nativeOf(position.network)
  const pnlTrend = Trend.of(pnlNative.pnlPercentage)
  const [closedAt] = useAtom(position.closedAt)
  const [remainingSizeCurrency] = useAtom(position.remainingSizeCurrency)
  const [remainingSizeUsd] = useAtom(position.remainingSizeUsd)

  const _renderClosedInformation = () => {
    return (
      <>
        <Pnl $trend={pnlTrend}>
          <Numeric
            preset="currency-amount-short"
            value={pnlNative.pnlNative}
            currency={nativeCurrency.ticker}
            forceSign
          />
          <PnlUsdText>
            {' '}
            (
            <Numeric
              preset="currency-amount-short"
              value={pnlUsd.pnlUsd}
              currency="$"
            />
            )
          </PnlUsdText>
        </Pnl>
        {closedAt ? (
          <ClosedText>
            {t('position.closed')} {format(closedAt, DATE_FORMAT.dd_MM_yy)}
          </ClosedText>
        ) : undefined}
      </>
    )
  }

  const renderActiveInformation = () => {
    return (
      <>
        <Size>
          <Numeric
            preset="currency-amount-short"
            value={remainingSizeCurrency}
            currency={pair.quote.ticker}
          />
        </Size>
        <SizeUsd>
          <Numeric
            currency="$"
            preset="currency-amount-short"
            value={remainingSizeUsd}
          />
        </SizeUsd>
      </>
    )
  }

  return <Root>{renderActiveInformation()}</Root>
}

const Root = styled.div`
  display: grid;
`

const Size = styled.div`
  color: var(--text-11);
  white-space: nowrap;

  font-size: 10px;
  font-weight: 600;
  line-height: 12px;
`

const Pnl = styled(Size)<{ $trend: Trend.Trend }>`
  color: ${({ $trend }) => trendColor($trend)};
`

const PnlUsdText = styled.span`
  font-size: 8px;
  font-weight: 400;
  line-height: 12px;
`

const SizeUsd = styled.div`
  color: var(--text-8);
  white-space: nowrap;
  font-size: 8px;
  font-weight: 500;
  line-height: 8px;
`

const ClosedText = styled(SizeUsd)``
